import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ErrorHandler, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild, inject, signal } from '@angular/core'
import { AbstractControl, ValidatorFn } from '@angular/forms'
import { FieldTree, SchemaPathTree, disabled, required, validate } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'

import { Lasku, LaskuBase, LaskunAsiakas, LaskunTyypit, LaskunTyyppi, TypeaheadAsiakas } from '@lemon/core-firebase/model/lasku'

import { BehaviorSubject, combineLatest, merge, Observable, ReplaySubject, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators'

import { AlvNumeronTarkistustulos } from '@lemon/core-firebase/model/common'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { MaaErikoiskoodit } from '@lemon/core-firebase/service/maa.service-base'
import { VatNumberValidationService } from '@lemon/core-firebase/service/vat-number-validation.service'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { StringService } from '@lemon/core/service/string.service'
import { arvonMuutokset, asetaArvo, nollaa } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../../_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from '../../../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../../../_jaettu-angular/service/maa.service'
import { AlvNumeronTarkistajaService } from '../../../_jaettu-angular/service/vat-number-check.service'
import { LaskuLomake } from '../../../laskut/lasku-rivit.component'
import { LaskunAsiakasTypeaheadBase } from '../../service/lasku/typeahead.service'

export type VatTaiYtunnuksePakollisuus = 'pakollinen-ytunnus' | 'pakollinen-vat' | 'ei-pakollinen' | 'ei-pakollinen-mutta-tarkista-vat-vai-ytunnus' | 'ei-pakollinen-mutta-tarkista-ulkomaan-vat'

function annaAsiakasNimiValidator(stringService: StringService): ValidatorFn {
  return (control: AbstractControl<TypeaheadAsiakas | string>) => {
    // No matter if object missin completely (null or undefined)
    // or empty string, required none the less
    if (!control.value) {
      return { required: true }
    }

    if (stringService.isString(control.value)) {
      if (control.value.trim().length < 2) {
        return { minlength: true }
      }
    } else if (typeof control.value === 'object') {
      // This first if also takes care of the case if the
      // object is of other shape. For an example typeof [] (array)
      // evaluates to 'object' too.
      if (!control.value.nimi || !stringService.isString(control.value.nimi)) {
        return { required: true }
      } else if (control.value.nimi.trim().length < 2) {
        return { minlength: true }
      }
    }

    // if (!kasiteltava?.asiakas || !kasiteltava.asiakas.nimi || !kasiteltava.asiakas.nimi.trim()) {
    //   return { required: true }
    // }

    // if (kasiteltava.asiakas.nimi.trim().length < 2) {
    //   return { minlength: true }
    // }

    return null
  }
}

/** Asiakkaan tietojen säännöt, joita komponentti ohjaa lomakkeen kenttien `ytunnuksenPakollisuus` ja `maaPoissa` kautta. */
export function laskunAsiakkaanSkeema(polku: SchemaPathTree<LaskuLomake>) {
  const stringService = inject(StringService)
  const codeCheckService = inject(CodeCheckService)
  const vatNumberValidationService = inject(VatNumberValidationService)
  const maaService = inject(MaaService)

  const ytunnusValidator = (ytunnus: string) => {
    if (ytunnus) {
      if (!codeCheckService.isValidYTunnus(ytunnus) && !vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, 'FI')) {
        return { kind: 'invalid_ytunnus' }
      }
    }
    return null
  }

  const onlyForeignVatNumberValidator = (ytunnus: string, maa: string) => {
    if (!maa || !ytunnus) {
      return null
    }
    const alpha2Code = maaService.getAlpha2Code(maa)
    if (alpha2Code !== 'FI' && vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, alpha2Code)) {
      return null
    }
    return { kind: 'invalid_vat_number' }
  }

  const vatNumberValidator = (ytunnus: string, maa: string) => {
    if (!maa || !ytunnus) {
      return null
    }
    const alpha2Code = maaService.getAlpha2Code(maa)
    if (vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, alpha2Code)) {
      return null
    }
    return { kind: 'invalid_vat_number' }
  }

  /** NB! Only use with LaskunTyyppi.TAVALLINEN */
  const vatNumberOrYtunnusValidator = (ytunnus: string) => {
    if (!ytunnus) {
      return null
    }

    if (codeCheckService.isValidYTunnus(ytunnus) || vatNumberValidationService.isValidEuVatNumberByCountry(ytunnus, 'FI')) {
      return null
    }

    return { kind: 'invalid_vat_number_or_ytunnus' }
  }

  required(polku.nimi)
  validoiReaktiivisesti(polku.nimi, annaAsiakasNimiValidator(stringService), { when: ({ value }) => !!value() })

  disabled(polku.ytunnus, ({ valueOf }) => valueOf(polku.ytunnuksenPakollisuus) === 'ei-pakollinen')
  required(polku.ytunnus, {
    when: ({ valueOf }) => valueOf(polku.ytunnuksenPakollisuus) === 'pakollinen-ytunnus' || valueOf(polku.ytunnuksenPakollisuus) === 'pakollinen-vat',
  })
  validate(polku.ytunnus, ({ value, valueOf }) => {
    switch (valueOf(polku.ytunnuksenPakollisuus)) {
      case 'pakollinen-ytunnus':
        return ytunnusValidator(value())
      case 'pakollinen-vat':
        return vatNumberValidator(value(), valueOf(polku.maa))
      case 'ei-pakollinen-mutta-tarkista-vat-vai-ytunnus':
        return vatNumberOrYtunnusValidator(value())
      case 'ei-pakollinen-mutta-tarkista-ulkomaan-vat':
        return onlyForeignVatNumberValidator(value(), valueOf(polku.maa))
    }
    return null
  })

  disabled(polku.maa, ({ valueOf }) => valueOf(polku.maaPoissa))
}

@Component({
  selector: 'app-lasku-asiakkaan-tiedot',
  templateUrl: './lasku-asiakkaan-tiedot.component.html',
  styleUrls: ['./lasku-asiakkaan-tiedot.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // ,
  // animations: [
  //   trigger('visibilityChanged', [
  //     state('shown' , style({ opacity: 1 })),
  //     state('hidden', style({ opacity: 0 })),
  //     state('quickhide', style({ opacity: 0 })),
  //     transition('shown => hidden', animate('2100ms')),
  //     transition('hidden => shown', animate('250ms')),
  //     transition('shown => quickhide', animate('1ms'))
  //   ])
  //   ,
  //   trigger('kuvake', [
  //     state('shown' , style({ })),
  //     state('hidden', style({ })),
  //     transition('* => shown', animate('700ms', keyframes([
  //       style({ transform: 'scale(1.3)' }),
  //       style({ transform: 'scale(0.7)' }),
  //       style({ transform: 'scale(1.3)' }),
  //       style({ transform: 'scale(1)' })
  //     ])))
  //   ])
  // ]
  standalone: false,
})
export class LaskuAsiakkaanTiedotComponent implements OnInit, OnDestroy {
  @ViewChild('asiakassearch', { read: MatInput, static: true }) materialInput: MatInput

  @Input() laskunTyyppiObservable: Observable<LaskunTyyppi>
  @Input() laskuObservable: Observable<{ juurilasku: Lasku; kasiteltava: LaskuBase }>
  @Input() parent: FieldTree<LaskuLomake>
  @Input() paivitaValidaatiot: any

  @Output() asiakasMuuttui = new EventEmitter<LaskunAsiakas>()

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public LaskunTyypit = LaskunTyypit

  readonly onkoYtunnusPakollinen = signal<VatTaiYtunnuksePakollisuus>('ei-pakollinen')
  suodatetutMaat: Observable<Maa[]>
  ytunnusPlaceholderObservable: Observable<string>
  suodatetutAsiakkaat: ReplaySubject<TypeaheadAsiakas[]> = new ReplaySubject<TypeaheadAsiakas[]>(1)
  vatTunnuksenValidointitulos: BehaviorSubject<string> = new BehaviorSubject('')
  asiakashaunVihjetekstiObservable: BehaviorSubject<string> = new BehaviorSubject('')
  // asiakastyyppiObservable: BehaviorSubject<LaskunAsiakastyyppi | null> = new BehaviorSubject(null)
  // asiakastyypinNakyvyysObservable: BehaviorSubject<'shown' | 'hidden' | 'quickhide'> = new BehaviorSubject<'shown' | 'hidden' | 'quickhide'>('shown')
  // asiakastyypinKuvakkeenNakyvyysObservable: BehaviorSubject<'shown' | 'hidden' | 'quickshow'> = new BehaviorSubject<'shown' | 'hidden' | 'quickshow'>('shown')
  readonly valittuAsiakas = signal(false)
  readonly naytaCheck = signal(false)

  private ngUnsubscribe = new Subject<void>()
  private ngUnsubscribeKomponentti = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _ytunnuksenUudelleenlahetys = new Subject<string>()

  constructor(
    private _errorHandler: ErrorHandler,
    private _changeDetectorRef: ChangeDetectorRef,
    private _validationService: FormValidationService,
    private _maaService: MaaService,
    private _alvNumeronTarkistajaService: AlvNumeronTarkistajaService,
    private _codeCheckService: CodeCheckService,
    private _laskunAsiakasTypeahead: LaskunAsiakasTypeaheadBase,
    private _laskuKopioija: LaskuKopioija,
    private _translationService: LemonTranslationService,
    private _vatNumberValidationService: VatNumberValidationService,
    private _stringService: StringService,
  ) {}

  // naytaAsiakastyyppiValitsin(event: Event) {
  //   if (event.preventDefault) {
  //     event.preventDefault()
  //   }
  //   if (event.stopImmediatePropagation) {
  //     event.stopImmediatePropagation()
  //   }
  //   if (event.stopPropagation) {
  //     event.stopPropagation()
  //   }
  //   this.asiakastyypinNakyvyysObservable.next('shown')
  // }

  focus() {
    setTimeout(() => {
      this.materialInput.focus()
      this._changeDetectorRef.markForCheck()
    }, 50)
  }

  tarkistaPoisto() {
    // console.log('tarkista poisto')
    if (!this.nimi().value()) {
      this.poistaValittu()
    }
  }

  poistaValittu() {
    this.tyhjennaAsiakashaunVihjeteksti()
    const s: LaskunAsiakas = {
      avain: null,
      nimi: '',
      ytunnus: '',
      katuosoite: '',
      postinro: '',
      postitmp: '',
      maa: this.maa().value(),
      date: null,
      laskunVastaanottajat: [],
      laskunKieli: null,
      laskunTyyppi: null,
      laskunValuutta: null,
      sahkoinenosoite: {
        sahkoinenOsoite: null,
        sahkoinenValittaja: null,
      },
      asiakastyyppi: null,
      viimeisinLaskuLahetetty: null,
    }
    nollaa(this.ytunnus, '')
    nollaa(this.katuosoite, '')
    nollaa(this.nimi, null)
    nollaa(this.postinro, '')
    nollaa(this.postitoimipaikka, '')
    nollaa(this.maa, null)
    // this.asiakastyyppi.reset()
    this.asiakasMuuttui.next(s)
  }

  ngOnDestroy() {
    this.ngUnsubscribeKomponentti.next()
    this.ngUnsubscribeKomponentti.complete()
    this.ngUnsubscribeKomponentti = new Subject<void>()
  }

  ngOnInit() {
    combineLatest([this.laskunTyyppiObservable, arvonMuutokset(this.maa, { injector: this._injector }).pipe(distinctUntilChanged())])
      .pipe(takeUntil(this.ngUnsubscribeKomponentti))
      .subscribe(([tyyppi, maakoodi]) => {
        // Aseta ytunnuksen pakollisuus
        if (tyyppi?.avain === LaskunTyypit.RAKENNUSALA.avain) {
          this.onkoYtunnusPakollinen.set('pakollinen-ytunnus')
        } else if (tyyppi?.avain === LaskunTyypit.EU_PALVELU.avain || tyyppi?.avain === LaskunTyypit.EU_TAVARA.avain) {
          this.onkoYtunnusPakollinen.set('pakollinen-vat')
        } else if (tyyppi?.avain === LaskunTyypit.TAVALLINEN.avain) {
          this.onkoYtunnusPakollinen.set('ei-pakollinen-mutta-tarkista-vat-vai-ytunnus')
        } else if (tyyppi?.avain === LaskunTyypit.TAVALLINEN_ULKOM.avain) {
          const onkoEunUlkopuolinenMaa = this._maaService.euMaakoodit.indexOf(maakoodi) < 0 && maakoodi !== MaaErikoiskoodit.POHJOIS_IRLANTI_KOODI_ALPHA_3
          if (onkoEunUlkopuolinenMaa) {
            // EU:n ulkopuolella ei tarvetta VAT:lle (MUU_MAAILMA laskun tyypissä)
            this.onkoYtunnusPakollinen.set('ei-pakollinen')
          } else {
            this.onkoYtunnusPakollinen.set('ei-pakollinen-mutta-tarkista-ulkomaan-vat')
          }
        } else {
          this.onkoYtunnusPakollinen.set('ei-pakollinen')
        }
        this.paivitaYtunnuksenPakollisuus(this.onkoYtunnusPakollinen())
        // console.log('Tyyppi päivittyi')
      })

    this.suodatetutMaat = combineLatest([this.laskuObservable, this._maaService.kaikkiMaatJaVeroalueetObservable]).pipe(
      map(([lasku, maat]) => {
        // Suodata maat tyypin mukaan
        const tyyppi = LaskunTyypit.annaLaskunTyyppi(lasku.kasiteltava.tyyppi)
        const suodatetut = this._suodataMaat(maat, tyyppi)
        this.asetaMaa(tyyppi, lasku.kasiteltava.asiakas, suodatetut)
        return suodatetut
      }),
    )

    this.ytunnusPlaceholderObservable = this.laskunTyyppiObservable.pipe(
      switchMap(tyyppi => {
        return this._translationService.currentLanguageObservable.pipe(
          map(kieli => {
            if (tyyppi.avain === LaskunTyypit.TAVALLINEN.avain || tyyppi.avain === LaskunTyypit.RAKENNUSALA.avain) {
              return this._translationService.lokalisoiKielella('lasku.ytunnus', kieli)
            }
            return this._translationService.lokalisoiKielella('lasku.vat-tunnus', kieli)
          }),
        )
      }),
    )

    this.laskuObservable.pipe(takeUntil(this.ngUnsubscribeKomponentti)).subscribe(lasku => {
      this.ngUnsubscribe.next()
      this.ngUnsubscribe.complete()
      this.ngUnsubscribe = new Subject<void>()
      this.valittuAsiakas.set(!!lasku.kasiteltava.asiakas.avain)
      this._setupForm(lasku.kasiteltava)
      if (this.valittuAsiakas()) {
        this.naytaCheck.set(true)
        this.asiakashaunVihjetekstiObservable.next(this._translationService.lokalisoi('lasku.tallennettu-asiakas'))
      } else {
        this.tyhjennaAsiakashaunVihjeteksti()
      }
      // console.log('Käsiteltävä päivittyi')
    })
  }

  private _asetaTypeaheadAsiakas(typeaheadAsiakas: TypeaheadAsiakas) {
    if (!typeaheadAsiakas || !typeaheadAsiakas.avain) {
      return
    }
    // this.parent.get('nimi').setValue(typeaheadAsiakas.nimi)
    asetaArvo(this.ytunnus, typeaheadAsiakas.ytunnus ?? '')
    this._laskunAsiakasTypeahead
      .annaAsiakas(typeaheadAsiakas)
      .then(asiakas => {
        if (asiakas) {
          this.tyhjennaAsiakashaunVihjeteksti()
          this.asiakasMuuttui.next(this._laskuKopioija.copyAsiakas(asiakas))
          this.suodatetutAsiakkaat.next([])
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
      })
  }

  private _suodataMaat(maat: Maa[], tyyppi: LaskunTyyppi): Maa[] {
    if (this._onkoKotimaanLasku(tyyppi)) {
      return maat.filter(maa => maa.koodi === 'FIN')
    } else if (this._onkoEuLasku(tyyppi)) {
      if (this._onkoPohjoisIrlantiLasku(tyyppi)) {
        return maat.filter(maa => (maa.koodi === MaaErikoiskoodit.POHJOIS_IRLANTI_KOODI_ALPHA_3 || this._maaService.euMaakoodit.indexOf(maa.koodi) > -1) && maa.koodi !== 'FIN')
      }
      return maat.filter(maa => this._maaService.euMaakoodit.indexOf(maa.koodi) > -1 && maa.koodi !== 'FIN')
    } else if (this._onkoEunUlkopuolinenLasku(tyyppi)) {
      return maat.filter(maa => this._maaService.euMaakoodit.indexOf(maa.koodi) < 0 && maa.koodi !== MaaErikoiskoodit.POHJOIS_IRLANTI_KOODI_ALPHA_3)
    } else if (this._onkoKotimaanLaskuUlkomaiselleYritykselle(tyyppi)) {
      return maat.filter(maa => maa.koodi !== 'FIN')
    }
    return maat
  }

  private _onkoKotimaanLasku(tyyppi: LaskunTyyppi): boolean {
    return tyyppi?.avain === LaskunTyypit.TAVALLINEN.avain || tyyppi?.avain === LaskunTyypit.RAKENNUSALA.avain
  }

  private _onkoKotimaanLaskuUlkomaiselleYritykselle(tyyppi: LaskunTyyppi): boolean {
    return tyyppi.avain === LaskunTyypit.TAVALLINEN_ULKOM.avain
  }

  /**
   * There is a special agreement that started validity 1.1.2021 and will be in effect for 4 years tha grants
   * Northern Ireland the right to be treated as EU country for VAT taxation purposes. The agreement is valid
   * only for goods. (Not services.)
   * @param tyyppi
   * @returns
   */
  private _onkoPohjoisIrlantiLasku(tyyppi: LaskunTyyppi): boolean {
    return (
      tyyppi.avain === LaskunTyypit.EU_TAVARA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_EI_REKISTEROITYNYT.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_TAVARA.avain
    )
  }

  private _onkoEuLasku(tyyppi: LaskunTyyppi): boolean {
    return (
      tyyppi.avain === LaskunTyypit.EU_TAVARA.avain ||
      tyyppi.avain === LaskunTyypit.EU_PALVELU.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_EI_REKISTEROITYNYT.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_TAVARA.avain ||
      tyyppi.avain === LaskunTyypit.EU_KULUTTAJA_PALVELU.avain
    )
  }

  private _onkoEunUlkopuolinenLasku(tyyppi: LaskunTyyppi): boolean {
    return tyyppi.avain === LaskunTyypit.MUU_MAAILMA.avain
  }

  private _setupForm(kasiteltava: LaskuBase) {
    if (kasiteltava && kasiteltava.asiakas) {
      const typeaheadAsiakas: TypeaheadAsiakas = {
        avain: kasiteltava.asiakas.avain,
        nimi: kasiteltava.asiakas.nimi,
        ytunnus: kasiteltava.asiakas.ytunnus,
      }

      asetaArvo(this.nimi, typeaheadAsiakas)
      asetaArvo(this.ytunnus, kasiteltava.asiakas.ytunnus ?? '')
      asetaArvo(this.katuosoite, kasiteltava.asiakas.katuosoite ?? '')
      asetaArvo(this.postinro, kasiteltava.asiakas.postinro ?? '')
      asetaArvo(this.postitoimipaikka, kasiteltava.asiakas.postitmp ?? '')
      asetaArvo(this.maa, kasiteltava.asiakas.maa ?? null)
      // this.asiakastyyppi.setValue(kasiteltava.asiakas.asiakastyyppi)

      // this.asiakastyypinNakyvyysObservable.next(kasiteltava.asiakas.asiakastyyppi ? 'quickhide' : 'shown')
      // this.asiakastyypinKuvakkeenNakyvyysObservable.next(kasiteltava.asiakas.asiakastyyppi ? 'quickshow' : 'hidden')
      // this.asiakastyyppiObservable.next(kasiteltava.asiakas.asiakastyyppi)

      // Aseta kuuntelijat
      const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this.ngUnsubscribe))
      muutokset(this.nimi).subscribe(value => {
        // // console.log('ASETA NIMI')

        if (this._stringService.isString(value)) {
          kasiteltava.asiakas.nimi = value
          this.asetaAsiakashaunVihjeteksti()
          if (this.valittuAsiakas()) {
          } else {
            const kasitelty = this._validationService.processValue(value)
            this._laskunAsiakasTypeahead
              .haeJaSuodata(kasitelty)
              .then(result => {
                this.suodatetutAsiakkaat.next(result.suodatetut)
              })
              .catch(err => {
                this._errorHandler.handleError(err)
              })
          }
        } else if (value) {
          if (this.valittuAsiakas()) {
            this.asetaAsiakashaunVihjeteksti()
            kasiteltava.asiakas.nimi = value.nimi
          } else {
            this._asetaTypeaheadAsiakas(value)
          }
        }
      })
      merge(muutokset(this.ytunnus), this._ytunnuksenUudelleenlahetys)
        .pipe(debounceTime(750), takeUntil(this.ngUnsubscribe))
        .subscribe(value => {
          // console.log('ytunnustarkistus', value)

          if (!value) {
            return
          }

          if (this.onkoYtunnusPakollinen() === 'pakollinen-ytunnus') {
            if (this._codeCheckService.isValidYTunnus(value) || this._vatNumberValidationService.isValidEuVatNumberByCountry(value, 'FI')) {
              this._tarkistaAlvNumeroViesista(this._codeCheckService.annaVatTunnus(value))
            } else if (this._vatNumberValidationService.isValidEuVatNumber(value)) {
              this._tarkistaAlvNumeroViesista(value)
            }
          } else if (
            (this.onkoYtunnusPakollinen() === 'pakollinen-vat' || this.onkoYtunnusPakollinen() === 'ei-pakollinen-mutta-tarkista-ulkomaan-vat') &&
            this._vatNumberValidationService.isValidEuVatNumber(value)
          ) {
            this._tarkistaAlvNumeroViesista(value)
          }
        })
      muutokset(this.ytunnus).subscribe(value => {
        // console.log('ytunnus')
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.ytunnus !== processed) {
          kasiteltava.asiakas.ytunnus = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.katuosoite).subscribe(value => {
        // console.log('katuosoite')
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.katuosoite !== processed) {
          kasiteltava.asiakas.katuosoite = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.postinro).subscribe(value => {
        // console.log('postinro')
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.postinro !== processed) {
          kasiteltava.asiakas.postinro = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.postitoimipaikka).subscribe(value => {
        // console.log('postitoimipaikka')
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.postitmp !== processed) {
          kasiteltava.asiakas.postitmp = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.maa).subscribe(value => {
        // console.log('maa')
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.maa !== processed) {
          kasiteltava.asiakas.maa = processed
          this.asetaAsiakashaunVihjeteksti()
          this._lahetaYtunnusUudelleen()
        }
      })
      // this.asiakastyyppi.valueChanges.pipe(
      //   takeUntil(this.ngUnsubscribe)
      // ).subscribe(value => {
      //   // console.log('asiakastyyppi', value)
      //   this.asetaAsiakashaunVihjeteksti()
      //   kasiteltava.asiakas.asiakastyyppi = this.validationService.processValue(value)
      //   this.asiakastyyppiObservable.next(kasiteltava.asiakas.asiakastyyppi)
      //   this.asiakastyypinNakyvyysObservable.next(kasiteltava.asiakas.asiakastyyppi ? 'hidden' : 'shown')
      //   this.asiakastyypinKuvakkeenNakyvyysObservable.next(kasiteltava.asiakas.asiakastyyppi ? 'shown' : 'hidden')
      // })

      // Aseta ytunnuksen pakollisuus
      this.paivitaYtunnuksenPakollisuus(this.onkoYtunnusPakollinen())
    }
  }

  private _tarkistaAlvNumeroViesista(value: string) {
    const kasitelty = this._validationService.processValue(value)
    this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.tarkistetaan'))
    const maakoodi = kasitelty.substr(0, 2)
    const numero = kasitelty.substr(2)
    this._alvNumeronTarkistajaService
      .tarkistaVatNumero(maakoodi, numero)
      .then(vastaus => {
        // // console.log(vastaus)
        if (vastaus.tulos === AlvNumeronTarkistustulos.ONNISTUI) {
          this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.oikein', { nimi: vastaus.yrityksenNimi, osoite: vastaus.yrityksenOsoite }))
          // this.vatTunnuksenValidointitulos = 'Numero oikein, tiedot: ' + vastaus.yrityksenNimi + ', ' + vastaus.yrityksenOsoite
        } else if (vastaus.tulos === AlvNumeronTarkistustulos.EU_PALVELIMEN_VIRHE) {
          this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.epaonnistui-eu'))
          // this.vatTunnuksenValidointitulos = 'VAT-numeron tarkistaminen ei onnistunut EU-palvelimen takia.'
        } else if (vastaus.tulos === AlvNumeronTarkistustulos.MAAKOODI_VIRHEELLINEN) {
          this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.virheellinen-maakoodi'))
          // this.vatTunnuksenValidointitulos = 'Virheellinen maakoodi. VAT-numero alkaa kaksikirjaimisella koodilla, esim. FI.'
        } else {
          this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.virheellinen-numero'))
          // this.vatTunnuksenValidointitulos = 'Virheellinen numero. Tarkista numero.'
        }
      })
      .catch(error => {
        this.vatTunnuksenValidointitulos.next(this._translationService.lokalisoi('lasku.alv-numeron-tarkistaminen.tuntematon-virhe'))
        // this.vatTunnuksenValidointitulos = 'Tarkistuksen aikana tapahtui virhe: ' + error.message
        this._errorHandler.handleError(error)
      })
  }

  private tyhjennaAsiakashaunVihjeteksti() {
    this.asiakashaunVihjetekstiObservable.next('')
  }

  private asetaAsiakashaunVihjeteksti() {
    if (this.valittuAsiakas()) {
      this.asiakashaunVihjetekstiObservable.next(this._translationService.lokalisoi('lasku.muokkaa-asiakasta'))
    } else {
      this.asiakashaunVihjetekstiObservable.next(this._translationService.lokalisoi('lasku.tallenna-uutena-asiakkaana'))
    }
    this.naytaCheck.set(false)
  }

  displayFn(laskunAsiakas: LaskunAsiakas): string {
    if (laskunAsiakas && laskunAsiakas.nimi) {
      return laskunAsiakas.nimi
    }
    return ''
  }

  private paivitaYtunnuksenPakollisuus(pakollinen: VatTaiYtunnuksePakollisuus) {
    asetaArvo(this.parent.ytunnuksenPakollisuus, pakollinen)
    this._lahetaYtunnusUudelleen()
  }

  // Y-tunnuksen pakollisuuden tai maan muuttuessa ALV-numero tarkistetaan uudelleen uusilla säännöillä, vaikka kentän
  // arvo pysyy samana. Muut y-tunnuksen kuuntelijat eivät reagoi samaan arvoon, joten vain tarkistus saa sen.
  private _lahetaYtunnusUudelleen() {
    this._ytunnuksenUudelleenlahetys.next(this.ytunnus().value())
  }

  get katuosoite() {
    return this.parent.katuosoite
  }

  get nimi() {
    return this.parent.nimi
  }

  get ytunnus() {
    return this.parent.ytunnus
  }

  get postinro() {
    return this.parent.postinro
  }

  get postitoimipaikka() {
    return this.parent.postitoimipaikka
  }

  get maa() {
    return this.parent.maa
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private asetaMaa(tyyppi: LaskunTyyppi, asiakas: LaskunAsiakas, suodatetutMaat: Maa[]) {
    // Aseta maa
    if (this._onkoKotimaanLasku(tyyppi)) {
      const maaKentta = this.maa
      asetaArvo(maaKentta, 'FIN')
      asetaArvo(this.parent.maaPoissa, true)
      if (asiakas) {
        asiakas.maa = 'FIN'
      }
    } else {
      const maaKentta = this.maa
      asetaArvo(this.parent.maaPoissa, false)
      if (asiakas) {
        if (suodatetutMaat.findIndex(maa => maa.koodi === asiakas.maa) < 0) {
          if (maaKentta) {
            asetaArvo(maaKentta, null)
          }
          asiakas.maa = null
        }
      }
    }
  }
}
